import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { HelpCircle, Pin, PinOff, ChevronDown, ChevronLeft, ChevronRight, Sun, Moon, Monitor } from 'lucide-react';
import { navForUser, MGR } from './nav/navConfig';
import useNavPrefs from '../hooks/useNavPrefs';

const API = `${window.__BACKEND_URL__}/api`;

const isPathActive = (path, location) => {
  const [base, query] = path.split('?');
  if (location.pathname !== base && !(base !== '/' && location.pathname.startsWith(`${base}/`))) return false;
  if (query) return location.search.includes(query);
  if (base === '/settings') return !/section=(customers|projects)/.test(location.search);
  return true;
};

export default function Sidebar({ collapsed, onToggle }) {
  const location = useLocation();
  const { user, token } = useAuth();
  const { mode, resolved, toggleMode } = useTheme();
  const { t } = useTranslation();
  const role = user?.role || 'user';
  const isAdmin = role === 'admin' || !!user?.is_super_user;
  const effectiveRole = isAdmin ? 'admin' : role;
  const nav = useMemo(() => navForUser(user), [user]);
  const allItems = useMemo(() => nav.flatMap((n) => (n.items ? n.items : [n])), [nav]);

  // Pins + group state: synced across devices via /api/me/nav-prefs (Sep 2026).
  const { pins, setPins, groups: openGroups, setGroups, loaded: prefsLoaded } = useNavPrefs(token);
  const togglePin = useCallback((key) => setPins(pins.includes(key) ? pins.filter((k) => k !== key) : [...pins, key]), [pins, setPins]);
  const pinnedItems = pins.map((k) => allItems.find((i) => i.key === k)).filter(Boolean);

  // Group open state (rule 5): remembered; on first load only the current page's group is open.
  const activeGroupKey = nav.find((n) => n.items && n.items.some((i) => isPathActive(i.path, location)))?.key || null;
  const loadedOnce = useRef(false);
  useEffect(() => {
    if (loadedOnce.current || !prefsLoaded) return;
    loadedOnce.current = true;
    setGroups(activeGroupKey ? { [activeGroupKey]: true } : {});
  }, [activeGroupKey, prefsLoaded, setGroups]);
  const toggleGroup = (key) => setGroups({ ...openGroups, [key]: !openGroups[key] });

  // Approvals badge (rule 4) — the only badge in the nav.
  const [approvalsCount, setApprovalsCount] = useState(0);
  useEffect(() => {
    if (!token || !MGR.includes(effectiveRole)) return undefined;
    let alive = true;
    const load = () => axios.get(`${API}/approvals/badge-count`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (alive) setApprovalsCount(r.data.count || 0); }).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { alive = false; clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [token, effectiveRole, location.pathname]);

  // Context menu (right-click → Pin / Unpin)
  const [ctx, setCtx] = useState(null);
  useEffect(() => {
    if (!ctx) return undefined;
    const close = () => setCtx(null);
    window.addEventListener('click', close);
    window.addEventListener('keydown', close);
    return () => { window.removeEventListener('click', close); window.removeEventListener('keydown', close); };
  }, [ctx]);

  const [flyout, setFlyout] = useState(null);
  const [leadsSquad, setLeadsSquad] = useState(false);
  useEffect(() => {
    if (!token || role !== 'user') return;
    fetch(`${API}/my/led-squads`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : { squads: [] })).then((d) => setLeadsSquad((d.squads || []).length > 0)).catch(() => {});
  }, [token, role]);
  const manualTab = isAdmin ? 'admin' : role === 'supervisor' ? 'supervisor' : leadsSquad ? 'squad' : 'user';

  const label = (n) => t(n.tKey, n.name);
  const nextModeLabel = { system: 'Day Mode', light: 'Night Mode', dark: 'System' }[mode];
  const ThemeIcon = mode === 'system' ? Monitor : mode === 'light' ? Sun : Moon;
  const iconColor = mode === 'system' ? (resolved === 'light' ? '#4A5568' : '#A0AEC0') : mode === 'light' ? '#FFA000' : '#A0AEC0';

  const Badge = ({ item }) => (item.badge === 'approvals' && approvalsCount > 0 ? (
    <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center bg-[#FFA000] text-[#0F1923]" data-testid="sidebar-approvals-badge">{approvalsCount > 99 ? '99+' : approvalsCount}</span>
  ) : null);

  // Every nav item is a real anchor (rule 6): middle-click / open-in-new-tab work.
  const NavItem = ({ item, nested = false, inFlyout = false }) => {
    const Icon = item.icon;
    const active = isPathActive(item.path, location);
    const pinned = pins.includes(item.key);
    const iconOnly = collapsed && !inFlyout;
    return (
      <div className="group/item relative" onContextMenu={(e) => { e.preventDefault(); setCtx({ x: e.clientX, y: e.clientY, key: item.key, pinned, name: label(item) }); }}>
        <Link
          to={item.path}
          data-testid={`sidebar-${item.key}`}
          aria-current={active ? 'page' : undefined}
          className={`w-full flex items-center gap-3 ${nested && !iconOnly ? 'pl-9 pr-3' : 'px-3'} py-2 rounded-xl text-sm font-medium transition-all duration-200 ${active ? 'text-white shadow-lg' : 'hover:bg-white/5'} ${iconOnly ? 'justify-center' : ''}`}
          style={active ? { background: '#1E88E5', boxShadow: '0 4px 16px rgba(30,136,229,0.4)' } : { color: '#A0AEC0' }}
          title={iconOnly ? label(item) : undefined}
          onClick={() => setFlyout(null)}
        >
          <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={active ? 2.5 : 1.5} />
          {!iconOnly && <span className="truncate">{label(item)}</span>}
          {!iconOnly && <Badge item={item} />}
          {iconOnly && item.badge === 'approvals' && approvalsCount > 0 && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#FFA000]" />}
        </Link>
        {!iconOnly && (
          <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(item.key); }}
            className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md transition-opacity ${pinned ? 'opacity-70' : 'opacity-0 group-hover/item:opacity-100'} hover:bg-white/10 ${item.badge === 'approvals' && approvalsCount > 0 ? 'right-9' : ''}`}
            style={{ color: active ? '#fff' : '#A0AEC0' }} title={pinned ? 'Unpin' : 'Pin to top'} aria-label={pinned ? `Unpin ${label(item)}` : `Pin ${label(item)}`} data-testid={`sidebar-pin-${item.key}`}>
            {pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
    );
  };

  const Group = ({ group }) => {
    const Icon = group.icon;
    const open = !!openGroups[group.key];
    const containsActive = group.items.some((i) => isPathActive(i.path, location));
    if (collapsed) {
      return (
        <div className="relative" onMouseEnter={() => setFlyout(group.key)} onMouseLeave={() => setFlyout(null)}>
          <button type="button" className={`w-full flex items-center justify-center px-3 py-2 rounded-xl transition-all ${containsActive ? 'text-white' : 'hover:bg-white/5'}`}
            style={{ color: containsActive ? '#1E88E5' : '#A0AEC0' }} title={label(group)} aria-haspopup="true" aria-expanded={flyout === group.key}
            onClick={() => setFlyout(flyout === group.key ? null : group.key)} data-testid={`sidebar-group-${group.key}`}>
            <Icon className="w-5 h-5" strokeWidth={containsActive ? 2.5 : 1.5} />
          </button>
          {flyout === group.key && (
            <div className="absolute left-full top-0 ml-1 w-52 rounded-xl p-2 z-[60] shadow-2xl" style={{ background: '#0F1923', border: '1px solid #2D3748' }} data-testid={`sidebar-flyout-${group.key}`}>
              <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: '#4A5568' }}>{label(group)}</p>
              <div className="space-y-0.5">{group.items.map((i) => <NavItem key={i.key} item={i} inFlyout />)}</div>
            </div>
          )}
        </div>
      );
    }
    return (
      <div>
        <button type="button" onClick={() => toggleGroup(group.key)} aria-expanded={open}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all hover:bg-white/5 ${containsActive && !open ? 'text-white' : ''}`}
          style={{ color: containsActive ? '#E2E8F0' : '#A0AEC0' }} data-testid={`sidebar-group-${group.key}`}>
          <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={containsActive ? 2.2 : 1.5} />
          <span className="truncate">{label(group)}</span>
          {group.key === 'people' && !open && approvalsCount > 0 && <span className="w-2 h-2 rounded-full bg-[#FFA000]" data-testid="sidebar-people-dot" />}
          <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && <div className="space-y-0.5 mt-0.5" data-testid={`sidebar-group-${group.key}-items`}>{group.items.map((i) => <NavItem key={i.key} item={i} nested />)}</div>}
      </div>
    );
  };

  return (
    <aside className={`fixed top-0 left-0 h-screen z-50 flex flex-col transition-all duration-300 ${collapsed ? 'w-[68px]' : 'w-[240px]'}`}
      style={{ background: '#0F1923', borderRight: '1px solid #2D3748' }} data-testid="sidebar">
      <div className={`flex items-center ${collapsed ? 'flex-col gap-2 py-3' : 'justify-between gap-2 px-4 h-16'} flex-shrink-0`}>
        <Link to="/home" className="flex items-center gap-3 min-w-0" data-testid="sidebar-logo">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#1E88E5' }}><span className="text-white font-extrabold text-base">H</span></div>
          {!collapsed && <span className="text-white font-bold text-lg tracking-tight whitespace-nowrap">Hines<span style={{ color: '#1E88E5' }}>Time</span></span>}
        </Link>
        <button type="button" onClick={toggleMode} className="p-2 rounded-xl transition-all hover:bg-white/5 flex-shrink-0" style={{ color: iconColor }}
          title={`Theme: ${mode === 'system' ? `System (${resolved})` : mode === 'light' ? 'Day Mode' : 'Night Mode'} — click for ${nextModeLabel}`}
          aria-label={`Toggle theme — next: ${nextModeLabel}`} data-testid="theme-toggle" data-theme-mode={mode} data-theme-resolved={resolved}>
          <ThemeIcon className="w-5 h-5" strokeWidth={mode === 'light' ? 2 : 1.8} />
        </button>
      </div>
      <div className="mx-3 border-b" style={{ borderColor: '#2D3748' }} />

      <nav className="flex-1 py-3 px-3 overflow-y-auto overflow-x-visible" data-testid="sidebar-nav">
        {pinnedItems.length > 0 && (
          <div className="mb-3" data-testid="sidebar-pinned">
            {!collapsed && <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] select-none" style={{ color: '#4A5568' }}>{t('nav.section.pinned', 'Pinned')}</p>}
            <div className="space-y-0.5">{pinnedItems.map((i) => <NavItem key={`pin-${i.key}`} item={i} />)}</div>
            <div className="mx-2 mt-2 border-b" style={{ borderColor: '#2D3748' }} />
          </div>
        )}
        <div className="space-y-0.5">
          {nav.map((n) => (n.items ? <Group key={n.key} group={n} /> : <NavItem key={n.key} item={n} />))}
        </div>
      </nav>

      <div className="p-3 flex-shrink-0" style={{ borderTop: '1px solid #2D3748' }}>
        <Link to={`/user-manual?tab=${manualTab}`}
          className={`w-full flex items-center gap-3 px-3 py-2 mb-1 rounded-xl text-sm font-medium transition-all duration-200 hover:bg-white/5 ${collapsed ? 'justify-center' : ''}`}
          style={{ color: '#A0AEC0' }} title={collapsed ? t('nav.help', 'Help') : undefined} data-testid="sidebar-help">
          <HelpCircle className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
          {!collapsed && <span className="truncate">{t('nav.help', 'Help')}</span>}
        </Link>
        <div className="flex items-center gap-2">
          {!collapsed && (
            <div className="flex items-center gap-3 flex-1 min-w-0 px-1">
              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#1E88E5' }}><span className="text-white text-sm font-bold">{(user?.name || 'U').charAt(0).toUpperCase()}</span></div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-semibold truncate">{user?.name}</p>
                <p className="text-xs truncate capitalize" style={{ color: '#A0AEC0' }}>{user?.role}</p>
              </div>
            </div>
          )}
          <button onClick={onToggle} className={`p-2 rounded-xl transition-all hover:bg-white/5 ${collapsed ? 'mx-auto' : ''}`} style={{ color: '#64748B' }}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} data-testid="sidebar-toggle">
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {ctx && (
        <div className="fixed z-[70] rounded-lg py-1 text-sm shadow-2xl" style={{ left: ctx.x, top: ctx.y, background: '#1A2433', border: '1px solid #2D3748', minWidth: 160 }} data-testid="sidebar-context-menu">
          <button type="button" className="w-full text-left px-3 py-1.5 hover:bg-white/5 flex items-center gap-2 text-slate-200" onClick={() => { togglePin(ctx.key); setCtx(null); }} data-testid="sidebar-context-pin">
            {ctx.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />} {ctx.pinned ? 'Unpin' : 'Pin'} “{ctx.name}”
          </button>
          <button type="button" className="w-full text-left px-3 py-1.5 hover:bg-white/5 text-slate-400" onClick={() => { const it = allItems.find((i) => i.key === ctx.key); if (it) window.open(it.path, '_blank'); setCtx(null); }} data-testid="sidebar-context-newtab">
            Open in new tab
          </button>
        </div>
      )}
    </aside>
  );
}
